import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Tabs, Button, Input, Select, Form, message, Space, Tag, Table,
  Typography, Divider, Badge, Tooltip, Modal, Popconfirm, Checkbox, Alert,
  Row, Col, Spin, Empty,
} from 'antd';
import {
  PlusOutlined, SaveOutlined, DeleteOutlined, CopyOutlined, ExportOutlined,
  CheckCircleOutlined, ClockCircleOutlined, EyeOutlined, EyeInvisibleOutlined,
  ReloadOutlined, ProjectOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const API = '/api/v1';
const h = () => ({
  Authorization: `Bearer ${localStorage.getItem('spo_auth_token')}`,
  'Content-Type': 'application/json',
});

const STATUS_COLOR = { draft: 'default', filled: 'blue', verified: 'green' };
const STATUS_LABEL = { draft: '草稿', filled: '已填寫', verified: '已驗證' };

// ── Dynamic field renderer ──────────────────────────────────────────────────
function SurveyField({ field, value, onChange, reveal }) {
  const { key, label, type, required, placeholder, options, default: defaultVal } = field;
  const val = value !== undefined ? value : (defaultVal || '');

  if (type === 'password') {
    return (
      <Form.Item label={
        <Space>
          {label}
          {field.sensitive && <Tag color="orange" style={{ fontSize: 10 }}>🔒 敏感</Tag>}
          {required && <Tag color="red" style={{ fontSize: 10 }}>必填</Tag>}
        </Space>
      }>
        {reveal
          ? <Input.Password value={val} onChange={e => onChange(key, e.target.value)}
              placeholder={placeholder || ''} visibilityToggle />
          : <Input.Password value={val} onChange={e => onChange(key, e.target.value)}
              placeholder={placeholder || ''} />
        }
      </Form.Item>
    );
  }
  if (type === 'textarea') {
    return (
      <Form.Item label={<>{label}{required && <Tag color="red" style={{ fontSize: 10, marginLeft: 4 }}>必填</Tag>}</>}>
        <TextArea rows={3} value={val} onChange={e => onChange(key, e.target.value)} placeholder={placeholder || ''} />
      </Form.Item>
    );
  }
  if (type === 'select') {
    return (
      <Form.Item label={<>{label}{required && <Tag color="red" style={{ fontSize: 10, marginLeft: 4 }}>必填</Tag>}</>}>
        <Select value={val || undefined} onChange={v => onChange(key, v)}
          placeholder={placeholder || `選擇${label}`} style={{ width: '100%' }}>
          {(options || []).map(o => <Select.Option key={o} value={o}>{o}</Select.Option>)}
        </Select>
      </Form.Item>
    );
  }
  if (type === 'multicheck') {
    const checked = Array.isArray(val) ? val : (val ? val.split(',') : (defaultVal || []));
    return (
      <Form.Item label={label}>
        <Checkbox.Group
          options={(options || []).map(o => ({ label: o, value: o }))}
          value={checked}
          onChange={v => onChange(key, v)}
        />
      </Form.Item>
    );
  }
  if (type === 'checkbox') {
    return (
      <Form.Item>
        <Checkbox checked={!!val} onChange={e => onChange(key, e.target.checked)}>
          {label} {required && <Tag color="red" style={{ fontSize: 10 }}>必填</Tag>}
        </Checkbox>
      </Form.Item>
    );
  }
  if (type === 'number') {
    return (
      <Form.Item label={<>{label}{required && <Tag color="red" style={{ fontSize: 10, marginLeft: 4 }}>必填</Tag>}</>}>
        <Input type="number" value={val} onChange={e => onChange(key, e.target.value)}
          placeholder={placeholder || defaultVal || ''} />
      </Form.Item>
    );
  }
  if (type === 'date') {
    return (
      <Form.Item label={label}>
        <Input type="date" value={val} onChange={e => onChange(key, e.target.value)} />
      </Form.Item>
    );
  }
  // default: text / email / url
  return (
    <Form.Item label={<>{label}{required && <Tag color="red" style={{ fontSize: 10, marginLeft: 4 }}>必填</Tag>}</>}>
      <Input type={type === 'email' ? 'email' : type === 'url' ? 'url' : 'text'}
        value={val} onChange={e => onChange(key, e.target.value)}
        placeholder={placeholder || ''} />
    </Form.Item>
  );
}

// ── Single survey form ───────────────────────────────────────────────────────
function SurveyForm({ survey, template, onSaved, projectName }) {
  const [values, setValues] = useState(survey?.values || {});
  const [status, setStatus] = useState(survey?.status || 'draft');
  const [notes, setNotes] = useState(survey?.notes || '');
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState(false);

  const handleChange = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      if (survey?.id) {
        // Update
        await fetch(`${API}/dev-surveys/${survey.id}`, {
          method: 'PUT', headers: h(),
          body: JSON.stringify({ values, status, notes }),
        });
        message.success('已儲存');
      } else {
        // Create
        const res = await fetch(`${API}/dev-surveys`, {
          method: 'POST', headers: h(),
          body: JSON.stringify({
            project_name: projectName,
            system_type: template.system_type,
            values, notes, status,
          }),
        });
        const d = await res.json();
        message.success(`已建立 (ID: ${d.id})`);
      }
      onSaved?.();
    } catch (e) { message.error('儲存失敗: ' + e.message); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    if (!survey?.id) { message.warning('請先儲存再匯出'); return; }
    const res = await fetch(`${API}/dev-surveys/${survey.id}/export-md`, { headers: h() });
    const md = await res.text();
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `survey_${template.system_type}_${projectName}.md`;
    a.click();
    URL.revokeObjectURL(url);
    message.success('已匯出 Markdown');
  };

  const fields = template?.fields || [];
  const filledCount = fields.filter(f => values[f.key]).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <Badge count={filledCount} color="#52c41a" overflowCount={99}>
            <Text strong>{template?.display_name || template?.system_type}</Text>
          </Badge>
          <Text type="secondary">({filledCount}/{fields.length} 已填)</Text>
        </Space>
        <Space>
          <Select value={status} onChange={setStatus} size="small" style={{ width: 100 }}>
            <Select.Option value="draft">草稿</Select.Option>
            <Select.Option value="filled">已填寫</Select.Option>
            <Select.Option value="verified">已驗證</Select.Option>
          </Select>
          <Tooltip title={reveal ? '隱藏密碼' : '顯示密碼'}>
            <Button size="small" icon={reveal ? <EyeInvisibleOutlined /> : <EyeOutlined />}
              onClick={() => setReveal(r => !r)} />
          </Tooltip>
          {survey?.id && (
            <Button size="small" icon={<ExportOutlined />} onClick={handleExport}>匯出 MD</Button>
          )}
          <Button type="primary" icon={<SaveOutlined />} size="small"
            loading={saving} onClick={handleSave}>儲存</Button>
        </Space>
      </div>

      <Form layout="vertical" size="small">
        {fields.map(f => (
          <SurveyField key={f.key} field={f} value={values[f.key]}
            onChange={handleChange} reveal={reveal} />
        ))}
        <Form.Item label="備註 (Claude Code 注意事項)">
          <TextArea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="特殊說明、防火牆規則、注意事項..." />
        </Form.Item>
      </Form>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function DevSurveyPage() {
  const [templates, setTemplates] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState('Comboware_CDP_2026');
  const [newProject, setNewProject] = useState('');
  const [projectInput, setProjectInput] = useState(false);
  const [activeTab, setActiveTab] = useState('surveys');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tmplRes, survRes] = await Promise.all([
        fetch(`${API}/dev-surveys/templates`, { headers: h() }).then(r => r.json()),
        fetch(`${API}/dev-surveys`, { headers: h() }).then(r => r.json()),
      ]);
      setTemplates(Array.isArray(tmplRes) ? tmplRes : []);
      setSurveys(Array.isArray(survRes) ? survRes : []);
    } catch (e) { message.error('載入失敗: ' + e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group surveys by project
  const projects = [...new Set(surveys.map(s => s.project_name))];
  const projectSurveys = surveys.filter(s => s.project_name === activeProject);

  // For each template, find if there's already a survey for the active project
  const surveyByType = {};
  projectSurveys.forEach(s => { surveyByType[s.system_type] = s; });

  // Tab items: one per system type
  const formTabs = templates.map(tmpl => ({
    key: tmpl.system_type,
    label: (
      <Space size={4}>
        <span>{tmpl.icon?.replace(/\s.*/,'')}</span>
        <span style={{ fontSize: 12 }}>{tmpl.display_name.replace(/^[^\s]+\s/,'')}</span>
        {surveyByType[tmpl.system_type] && (
          <Tag color={STATUS_COLOR[surveyByType[tmpl.system_type].status] || 'default'}
            style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
            {STATUS_LABEL[surveyByType[tmpl.system_type].status]}
          </Tag>
        )}
      </Space>
    ),
    children: (
      <SurveyForm
        key={`${activeProject}-${tmpl.system_type}`}
        survey={surveyByType[tmpl.system_type]}
        template={tmpl}
        projectName={activeProject}
        onSaved={fetchAll}
      />
    ),
  }));

  // All surveys table
  const tableColumns = [
    { title: '專案', dataIndex: 'project_name', key: 'pn', width: 180, ellipsis: true },
    { title: '系統', dataIndex: 'system_type', key: 'st', width: 100,
      render: st => {
        const t = templates.find(t => t.system_type === st);
        return <Tag>{t?.icon?.split(' ')[0]} {st}</Tag>;
      }},
    { title: '狀態', dataIndex: 'status', key: 's', width: 80,
      render: s => <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag> },
    { title: '更新時間', dataIndex: 'updated_at', key: 'u', width: 140,
      render: v => v ? new Date(v).toLocaleString('zh-TW') : '-' },
    { title: '操作', key: 'act', width: 120,
      render: (_, r) => (
        <Space>
          <Button size="small" onClick={() => { setActiveProject(r.project_name); setActiveTab('fill'); }}>
            開啟
          </Button>
          <Popconfirm title="確定刪除？" onConfirm={async () => {
            await fetch(`${API}/dev-surveys/${r.id}`, { method: 'DELETE', headers: h() });
            message.success('已刪除'); fetchAll();
          }}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )},
  ];

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <ProjectOutlined style={{ marginRight: 8, color: '#722ed1' }} />
            開發配置調查表
          </Title>
          <Text type="secondary">
            標準化系統連線資訊收集 — 填寫後 Claude Code 直接讀取開發，可複製給新客戶使用
          </Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchAll} loading={loading}>重新整理</Button>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
        // ── Tab 1: 填寫調查表 ──
        {
          key: 'fill',
          label: <span>📝 填寫調查表</span>,
          children: (
            <div>
              {/* Project selector */}
              <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap>
                  <Text strong>專案：</Text>
                  {projects.map(p => (
                    <Tag key={p}
                      color={p === activeProject ? 'purple' : 'default'}
                      style={{ cursor: 'pointer', fontSize: 13, padding: '4px 10px' }}
                      onClick={() => setActiveProject(p)}>
                      {p === activeProject ? '✓ ' : ''}{p}
                    </Tag>
                  ))}
                  {projectInput ? (
                    <Space>
                      <Input size="small" value={newProject} onChange={e => setNewProject(e.target.value)}
                        placeholder="新專案名稱" style={{ width: 200 }}
                        onPressEnter={() => { if (newProject.trim()) { setActiveProject(newProject.trim()); setProjectInput(false); setNewProject(''); } }} />
                      <Button size="small" type="primary" onClick={() => {
                        if (newProject.trim()) { setActiveProject(newProject.trim()); setProjectInput(false); setNewProject(''); }
                      }}>確認</Button>
                      <Button size="small" onClick={() => setProjectInput(false)}>取消</Button>
                    </Space>
                  ) : (
                    <Button size="small" icon={<PlusOutlined />} onClick={() => setProjectInput(true)}>
                      新專案
                    </Button>
                  )}
                </Space>
              </Card>

              <Alert
                type="info" showIcon style={{ marginBottom: 16 }}
                message={`目前填寫專案：${activeProject}`}
                description="每個分頁對應一個系統，填寫後點「儲存」。管理員可點「匯出 MD」讓 Claude Code 讀取。🔒 密碼欄位存入時會混淆處理。"
              />

              {loading ? <Spin /> : (
                <Card bodyStyle={{ padding: '0 16px 16px' }}>
                  <Tabs
                    tabPosition="left"
                    items={formTabs}
                    style={{ minHeight: 500 }}
                  />
                </Card>
              )}
            </div>
          ),
        },

        // ── Tab 2: 所有調查表記錄 ──
        {
          key: 'surveys',
          label: <span>📋 所有記錄 <Badge count={surveys.length} color="#722ed1" /></span>,
          children: (
            <div>
              <Alert type="success" showIcon style={{ marginBottom: 16 }}
                message="這些調查表記錄是 Claude Code 開發的依據"
                description="管理員可在「填寫調查表」分頁點「匯出 MD」，將任一調查表匯出為 Markdown，上傳到 API 文件庫後 Claude Code 即可直接閱讀開發。未來複製同一份範本給新客戶，只需建立新專案填寫一次。" />
              <Table
                dataSource={surveys}
                rowKey="id"
                columns={tableColumns}
                loading={loading}
                pagination={{ pageSize: 20 }}
                size="small"
              />
            </div>
          ),
        },

        // ── Tab 3: 範本管理 ──
        {
          key: 'templates',
          label: <span>🗂️ 系統範本</span>,
          children: (
            <div>
              <Alert type="info" showIcon style={{ marginBottom: 16 }}
                message="可重複使用的系統範本"
                description="每個系統類型（Odoo、MSSQL、NAS 等）都有對應範本。新客戶使用同一套系統，直接在「填寫調查表」建立新專案，無需修改範本。" />
              <Row gutter={[12, 12]}>
                {templates.map(t => (
                  <Col span={8} key={t.system_type}>
                    <Card size="small" title={
                      <Space>
                        <span>{t.icon?.split(' ')[0]}</span>
                        <span>{t.display_name.replace(/^[^\s]+\s/,'')}</span>
                      </Space>
                    }>
                      <Text type="secondary" style={{ fontSize: 12 }}>{t.description}</Text>
                      <Divider style={{ margin: '8px 0' }} />
                      <Space wrap>
                        {(t.fields || []).map(f => (
                          <Tag key={f.key} color={f.sensitive || f.type === 'password' ? 'orange' : 'default'}
                            style={{ fontSize: 11 }}>
                            {f.sensitive || f.type === 'password' ? '🔒 ' : ''}{f.label}
                            {f.required ? ' *' : ''}
                          </Tag>
                        ))}
                      </Space>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          ),
        },
      ]} />
    </div>
  );
}
